import { useMemo } from "react";
import ReactSelect, {
    GroupBase,
    Props as ReactSelectProps,
    ClassNamesConfig,
} from "react-select";
import mergeClasses from "@/utils/merge-classes.ts";

export type SelectProps<
    Option = unknown,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
> = ReactSelectProps<Option, IsMulti, Group>;

const defaultClassNames = {
    control: () => "!min-h-0 !rounded-md !border-gray-200",
    menu: () => "text-sm",
    menuList: () => "text-sm",
    singleValue: () => "text-sm",
    placeholder: () => "text-sm",
    indicatorsContainer: () => "-my-1",
};

const Select = <
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>({
    isSearchable = false,
    menuPosition = "fixed",
    classNames,
    ...props
}: SelectProps<Option, IsMulti, Group>) => {
    const mergedClassNames = useMemo<ClassNamesConfig<Option, IsMulti, Group>>(() => {
        const allKeys = Array.from(
            new Set([
                ...Object.keys(defaultClassNames),
                ...(classNames ? Object.keys(classNames) : []),
            ])
        );

        const result: Record<string, (state: unknown) => string> = {};

        allKeys.forEach((key) => {
            result[key] = (state: unknown) => {
                const defaultFn = (defaultClassNames as Record<string, unknown>)[key];
                const customFn = classNames ? (classNames as Record<string, unknown>)[key] : undefined;

                const defaultClass = typeof defaultFn === "function" ? defaultFn(state) : defaultFn;
                const customClass = typeof customFn === "function" ? customFn(state) : customFn;

                return mergeClasses(
                    typeof defaultClass === "string" ? defaultClass : undefined,
                    typeof customClass === "string" ? customClass : undefined
                );
            };
        });

        return result as unknown as ClassNamesConfig<Option, IsMulti, Group>;
    }, [classNames]);

    return (
        <ReactSelect
            isSearchable={isSearchable}
            menuPosition={menuPosition}
            classNames={mergedClassNames}
            {...props}
        />
    );
};

export default Select;
